import { message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { TickCircle, TickSquare } from 'iconsax-react';
import { useApprovePromotionMutation } from '../../../../store/services/promotionsService';
import Button from '../../../../components/atoms/Button';
import TitleText from '../../../../components/atoms/TitleText';
import InfoText from '../../../../components/atoms/InfoText';

interface PromotionApproveModalProps {
  isOpen: boolean;
  onClose: (success?: boolean) => void;
  promotionId: string;
}

export default function PromotionApproveModal({
  isOpen,
  onClose,
  promotionId,
}: PromotionApproveModalProps) {
  const { t } = useTranslation();
  const [approvePromotion, { isLoading }] = useApprovePromotionMutation();

  const handleApprove = async () => {
    if (!promotionId) return;
    try {
      await approvePromotion(promotionId).unwrap();
      message.success(t('home.requestApprovedSuccessfully', 'Request approved successfully'));
      onClose(true);
    } catch (error: any) {
      const errData = error?.data;
      const errorDetail = errData?.errors
        ? (Object.values(errData.errors)[0] as string)
        : null;
      message.error(
        errorDetail ||
          errData?.message ||
          t('home.somethingWentWrong', 'Something went wrong')
      );
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      closable={false}
      footer={null}
      centered
      destroyOnHidden
      width={600}
      data-test="promo-approve-modal"
    >
      <div
        className="flex flex-col items-center justify-center gap-4 py-2"
        data-test="promo-approve-modal-content"
      >
        {/* Icon */}
        <div
          className="bg-green-50 w-12 h-12 rounded-xl flex justify-center items-center"
          data-test="promo-approve-modal-icon"
        >
          <TickSquare size={24} color="#16A34A" />
        </div>

        {/* Title and Description */}
        <div className="flex flex-col gap-1 items-center text-center">
          <TitleText
            text={t('home.approveRequest')}
            className="!font-semibold"
          />
          <InfoText
            text={t('home.approveRequestDescription')}
            maxLength="FULL"
          />
        </div>

        {/* Buttons */}
        <div
          className="flex justify-center w-full gap-3 mt-2"
          data-test="promo-approve-modal-actions"
        >
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            data-test="promo-approve-modal-cancel-btn"
          >
            {t('home.cancel')}
          </Button>
          <Button
            type="button"
            variant="success"
            icon={<TickCircle size={18} color="#ffffff" />}
            onClick={handleApprove}
            loading={isLoading}
            className="w-40"
            data-test="promo-approve-modal-submit-btn"
          >
            {t('home.promo-action-approve')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
